import { RING_ICONS } from "../shell/ring/ring-icons";
import { statusAction } from "./status-graph";

/**
 * **A visit's next step, for slot 1 of its ring** (QR9, STORY-221) — shared by
 * Home's row and the calendar's block so the two never word it differently.
 *
 * Worded by `status-graph.ts`, the same words the panel's buttons use:
 * Check in → Start treatment → Complete visit. `to` is the status moves the
 * step makes, in order. A not-yet-confirmed patient who is standing at the
 * desk is confirmed and checked in in one go, which is the two moves the
 * panel would take one click each (the prototype's `nextStep`).
 *
 * `COMPLETED` is never sent straight: completing charts and bills, so the
 * caller opens the calendar's own Complete visit dialog for it.
 *
 * A no-show, a completed and a cancelled visit have no next step here.
 */
export interface VisitStep {
  label: string;
  hint: string;
  icon: string;
  to: readonly string[];
}

export function visitStep(status: string): VisitStep | null {
  switch (status) {
    case "NOT_CHECKED_IN":
      return {
        label: statusAction("CHECKED_IN", "CONFIRMED"),
        hint: "Confirms and checks in",
        icon: RING_ICONS.arrive,
        to: ["CONFIRMED", "CHECKED_IN"],
      };
    case "CONFIRMED":
      return {
        label: statusAction("CHECKED_IN", "CONFIRMED"),
        hint: "They are here",
        icon: RING_ICONS.arrive,
        to: ["CHECKED_IN"],
      };
    case "CHECKED_IN":
      return {
        label: statusAction("IN_CHAIR", "CHECKED_IN"),
        hint: "Seats them in the chair",
        icon: RING_ICONS.seat,
        to: ["IN_CHAIR"],
      };
    case "IN_CHAIR":
      return {
        label: statusAction("COMPLETED", "IN_CHAIR"),
        hint: "What was done, then the bill",
        icon: RING_ICONS.finish,
        to: ["COMPLETED"],
      };
    default:
      return null;
  }
}

/** Not yet arrived — the only states a no-show or a reminder makes sense from. */
export function beforeArrival(status: string): boolean {
  return status === "NOT_CHECKED_IN" || status === "CONFIRMED";
}

/** What the quick ring can ask a visit's panel to do through its own buttons. */
export type PanelRingKey = "step" | "cancel" | "remind" | "pay";

/** One such request — the nonce lets the same request be made twice in a row. */
export interface PanelRingCommand {
  id: string;
  key: PanelRingKey;
  nonce: number;
}
